import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Percent, CreditCard, PlusCircle, History, Clock, HandCoins, Calculator } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SubHeader, Row, StatusBadge, EmptyState } from "@/components/Brand";
import { useApi } from "@/lib/api";
import { rp, fmtDate } from "@/lib/format";

const ActiveLoan = ({ loan, onPay }) => (
  <>
    <div className="bg-gradient-to-br from-blue-700 via-blue-600 to-sky-400 text-white rounded-2xl shadow-blue-500/30 p-5 shadow-lg fade-up" data-testid="active-loan-card">
      <div className="flex justify-between items-start">
        <div>
          <div className="text-sm text-blue-100">Pinjaman Aktif</div>
          <div className="text-3xl font-bold font-display" data-testid="active-loan-amount">{rp(loan.amount)}</div>
        </div>
        <span className="bg-emerald-500 text-white text-xs font-bold px-2.5 py-1 rounded-md">Lancar</span>
      </div>
      <div className="h-3 bg-white/25 rounded-full mt-4 overflow-hidden">
        <div className="h-full bg-emerald-400 rounded-full transition-[width] duration-700" style={{ width: `${loan.progress}%` }} data-testid="active-loan-progress" />
      </div>
      <div className="grid grid-cols-2 mt-4 text-sm">
        <div className="border-r border-white/25 pr-3">
          <div className="text-blue-100 text-xs">Sisa Pinjaman</div>
          <div className="font-bold" data-testid="active-loan-remaining">{rp(loan.remaining)}</div>
          <div className="text-xs text-blue-100">({(100 - loan.progress).toFixed(2)}%)</div>
        </div>
        <div className="pl-4 text-right">
          <div className="text-blue-100 text-xs">Sudah Dibayar</div>
          <div className="font-bold" data-testid="active-loan-paid">{rp(loan.paid_amount)}</div>
          <div className="text-xs text-blue-100">({loan.progress.toFixed(2)}%)</div>
        </div>
      </div>
    </div>
    <div className="bg-white rounded-2xl border border-slate-200 px-4">
      <Row icon={CalendarDays} label="Plafon Pinjaman" value={rp(loan.amount)} />
      <Row icon={CalendarDays} label="Tenor" value={`${loan.tenor_months} Bulan`} />
      <Row icon={Percent} label="Bunga" value={`${loan.rate}% / Bulan`} />
      <Row icon={CalendarDays} label="Jatuh Tempo Berikutnya" value={fmtDate(loan.next_due)} testid="active-loan-next-due" />
      <Row icon={CreditCard} label="Angsuran per Bulan" value={rp(loan.monthly_installment)} testid="active-loan-installment" />
    </div>
    <button onClick={onPay} data-testid="pay-installment-button"
      className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 active:scale-[0.98] text-white font-semibold py-3.5 rounded-xl shadow-md transition-[background-color,transform]">
      <CreditCard className="h-5 w-5" /> Bayar Angsuran
    </button>
  </>
);

export default function Loans() {
  const navigate = useNavigate();
  const { data } = useApi("/loans");
  const [history, setHistory] = useState(false);
  if (!data) return null;
  const active = data.find((l) => l.status === "aktif");
  const pending = data.find((l) => l.status === "pending");

  return (
    <div>
      <SubHeader title="Pinjaman Saya" onBack={() => navigate("/app")} />
      <div className="px-5 space-y-4">
        {active && <ActiveLoan loan={active} onPay={() => navigate(`/app/transaksi/bayar?jenis=angsuran&loan=${active.loan_id}`)} />}
        {pending && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex gap-3" data-testid="pending-loan-card">
            <Clock className="h-6 w-6 text-amber-600 shrink-0" />
            <div>
              <div className="font-semibold text-sm text-slate-900">Pengajuan {rp(pending.amount)} sedang ditinjau</div>
              <div className="text-xs text-slate-600">{pending.tenor_months} bulan • angsuran {rp(pending.monthly_installment)}/bulan</div>
            </div>
          </div>
        )}
        {!active && !pending && (
          <EmptyState testid="no-loan-state" icon={HandCoins} title="Belum ada pinjaman aktif" text="Hitung simulasi angsuran lalu ajukan pinjaman ke pengurus CU." />
        )}

        <div className="grid grid-cols-3 gap-3">
          <button onClick={() => navigate("/app/pinjaman/ajukan")} data-testid="apply-loan-button" className="bg-white border border-slate-200 rounded-2xl py-4 flex flex-col items-center gap-1.5 hover:shadow-md transition-shadow">
            <PlusCircle className="h-7 w-7 text-blue-800" strokeWidth={1.7} /><span className="text-xs font-semibold text-center">Ajukan Pinjaman Baru</span>
          </button>
          <button onClick={() => navigate("/app/pinjaman/ajukan?mode=simulasi")} data-testid="loan-simulator-button" className="bg-white border border-slate-200 rounded-2xl py-4 flex flex-col items-center gap-1.5 hover:shadow-md transition-shadow">
            <Calculator className="h-7 w-7 text-blue-800" strokeWidth={1.7} /><span className="text-xs font-semibold text-center">Simulasi Kredit</span>
          </button>
          <button onClick={() => setHistory(true)} data-testid="loan-history-button" className="bg-white border border-slate-200 rounded-2xl py-4 flex flex-col items-center gap-1.5 hover:shadow-md transition-shadow">
            <History className="h-7 w-7 text-blue-800" strokeWidth={1.7} /><span className="text-xs font-semibold text-center">Riwayat Pinjaman</span>
          </button>
        </div>
      </div>

      <Dialog open={history} onOpenChange={setHistory}>
        <DialogContent className="max-w-sm" data-testid="loan-history-dialog">
          <DialogHeader><DialogTitle>Riwayat Pinjaman</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {data.length === 0 && <p className="text-sm text-slate-500">Belum ada riwayat.</p>}
            {data.map((l) => (
              <div key={l.loan_id} className="border border-slate-200 rounded-xl p-3">
                <div className="flex justify-between items-center"><b className="text-sm">{rp(l.amount)}</b><StatusBadge status={l.status} /></div>
                <div className="text-xs text-slate-500 mt-1">{l.tenor_months} bln • {l.purpose} • {fmtDate(l.created_at)}</div>
                {l.admin_note && <div className="text-xs text-slate-700 mt-1">Catatan: {l.admin_note}</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
